import { SupportedLocale } from '../types';

/**
 * IANA timezones for the contiguous United States, Alaska, and Hawaii.
 */
const US_TIMEZONES = new Set([
  // Eastern
  'America/New_York',
  'America/Detroit',
  'America/Indiana/Indianapolis',
  'America/Indiana/Knox',
  'America/Indiana/Marengo',
  'America/Indiana/Petersburg',
  'America/Indiana/Tell_City',
  'America/Indiana/Vevay',
  'America/Indiana/Vincennes',
  'America/Indiana/Winamac',
  'America/Kentucky/Louisville',
  'America/Kentucky/Monticello',
  // Central
  'America/Chicago',
  'America/Menominee',
  'America/North_Dakota/Beulah',
  'America/North_Dakota/Center',
  'America/North_Dakota/New_Salem',
  // Mountain
  'America/Denver',
  'America/Boise',
  'America/Phoenix',
  // Pacific
  'America/Los_Angeles',
  // Alaska
  'America/Anchorage',
  'America/Juneau',
  'America/Metlakatla',
  'America/Nome',
  'America/Sitka',
  'America/Yakutat',
  // Hawaii-Aleutian
  'America/Adak',
  'Pacific/Honolulu',
]);

/**
 * IANA timezones covering all Canadian provinces and territories.
 */
const CA_TIMEZONES = new Set([
  // Newfoundland
  'America/St_Johns',
  // Atlantic
  'America/Halifax',
  'America/Glace_Bay',
  'America/Goose_Bay',
  'America/Moncton',
  // Eastern
  'America/Toronto',
  'America/Nipigon',
  'America/Thunder_Bay',
  'America/Iqaluit',
  'America/Pangnirtung',
  // Central
  'America/Winnipeg',
  'America/Rainy_River',
  'America/Rankin_Inlet',
  'America/Resolute',
  'America/Regina',
  'America/Swift_Current',
  // Mountain
  'America/Edmonton',
  'America/Cambridge_Bay',
  'America/Inuvik',
  'America/Yellowknife',
  'America/Creston',
  'America/Dawson_Creek',
  'America/Fort_Nelson',
  // Pacific
  'America/Vancouver',
  'America/Whitehorse',
  'America/Dawson',
]);

/**
 * BCP 47 language tags whose region component maps to the United States.
 * navigator.language returns the user's preferred browser language (e.g. "en-US").
 */
const US_LOCALES = new Set(['en-US', 'es-US']);

/**
 * BCP 47 language tags whose region component maps to Canada.
 */
const CA_LOCALES = new Set(['en-CA', 'fr-CA']);

/**
 * An axis-aligned rectangular bounding box defined by latitude/longitude
 * extremes.  Used for fast, approximate point-in-region checks.
 *
 * Coordinates follow the standard geographic convention:
 * - Latitude: south-negative, north-positive (−90 to +90).
 * - Longitude: west-negative, east-positive (−180 to +180).
 */
type LatLonBounds = {
  /** Southern boundary (inclusive). */
  latMin: number;
  /** Northern boundary (inclusive). */
  latMax: number;
  /** Western boundary (inclusive). */
  lonMin: number;
  /** Eastern boundary (inclusive). */
  lonMax: number;
};

/**
 * Approximate bounding boxes for the contiguous United States, Alaska,
 * and Hawaii.
 *
 * Note on the US/Canada border overlap: the US continental box extends to
 * 49.384358°N, and the Canadian box starts at 41.676555°N.  Southern Ontario,
 * Quebec, and the Maritimes therefore fall inside both boxes.  {@link
 * resolveLocaleFromCoords} checks US first, so those coordinates resolve to
 * `UnitedStates` — acceptable because the goal is "supported region"
 * confirmation, not precise US-vs-CA attribution.
 */
const US_GEO_BOUNDS: LatLonBounds[] = [
  // Continental United States
  {
    latMin: 24.396308,
    latMax: 49.384358,
    lonMin: -124.848974,
    lonMax: -66.93457,
  },
  // Alaska
  {
    latMin: 51.175092,
    latMax: 71.352561,
    lonMin: -179.148909,
    lonMax: -129.993,
  },
  // Hawaii
  {
    latMin: 18.910361,
    latMax: 28.402123,
    lonMin: -178.334698,
    lonMax: -154.806773,
  },
];

/**
 * Approximate bounding box for Canada.
 *
 * The southern edge (41.676555°N) is below the 49th parallel to capture the
 * Windsor/Ontario peninsula and other southerly Canadian geography, at the
 * cost of overlapping with the US continental box.  See the note on
 * {@link US_GEO_BOUNDS} for how the overlap is resolved.
 */
const CA_GEO_BOUNDS: LatLonBounds[] = [
  {
    latMin: 41.676555,
    latMax: 83.337609,
    lonMin: -141.002197,
    lonMax: -52.648099,
  },
];

/**
 * Returns `true` if `(lat, lon)` falls inside any of the provided bounding
 * boxes.  All comparisons are inclusive of the boundary edges.
 *
 * @param lat - Latitude in decimal degrees (positive = north).
 * @param lon - Longitude in decimal degrees (negative = west).
 * @param bounds - Array of {@link LatLonBounds} rectangles to test against.
 */
function isInBounds(lat: number, lon: number, bounds: LatLonBounds[]): boolean {
  return bounds.some(
    (b) =>
      lat >= b.latMin && lat <= b.latMax && lon >= b.lonMin && lon <= b.lonMax,
  );
}

/**
 * Maps a latitude/longitude coordinate pair to a {@link SupportedLocale} using
 * rectangular bounding boxes.
 *
 * Evaluation order matters: US bounds are checked before CA bounds.
 * Coordinates in the southern Canada/US border overlap (e.g. Windsor, ON at
 * ~42.3°N) will resolve to `UnitedStates` rather than `Canada` — acceptable
 * because both are supported regions and the distinction does not affect
 * feature gating.
 *
 * @param latitude  - GPS latitude in decimal degrees (positive = north).
 * @param longitude - GPS longitude in decimal degrees (negative = west).
 * @returns `UnitedStates` if coords are within {@link US_GEO_BOUNDS},
 *          `Canada` if within {@link CA_GEO_BOUNDS},
 *          `Unsupported` otherwise.
 */
export function resolveLocaleFromCoords(
  latitude: number,
  longitude: number,
): SupportedLocale {
  if (isInBounds(latitude, longitude, US_GEO_BOUNDS)) {
    return SupportedLocale.UnitedStates;
  }
  if (isInBounds(latitude, longitude, CA_GEO_BOUNDS)) {
    return SupportedLocale.Canada;
  }
  return SupportedLocale.Unsupported;
}

/**
 * Discriminated error code for {@link GeolocationDetectionError}.
 */
export enum GeolocationDetectionErrorCode {
  /** `navigator.geolocation` is not exposed by the browser. */
  NotSupported = 'NOT_SUPPORTED',
  /** The user denied the location permission prompt. */
  PermissionDenied = 'PERMISSION_DENIED',
  /** The device could not acquire a GPS fix. */
  PositionUnavailable = 'POSITION_UNAVAILABLE',
  /** The 10-second acquisition window elapsed. */
  Timeout = 'TIMEOUT',
}

/**
 * Thrown by {@link detectLocaleFromGeolocation} when the browser Geolocation
 * API fails to return a position fix.
 *
 * Distinguished from a successful result of {@link SupportedLocale.Unsupported}
 * (GPS worked but coordinates are outside US/CA) so callers can show targeted
 * error messages rather than silently treating the user as unsupported.
 */
export class GeolocationDetectionError extends Error {
  public readonly code: GeolocationDetectionErrorCode;

  constructor(code: GeolocationDetectionErrorCode) {
    super(`Geolocation detection failed: ${code}`);
    this.name = 'GeolocationDetectionError';
    this.code = code;
  }
}

/**
 * Requests a one-shot GPS position fix and maps it to a {@link SupportedLocale}
 * via {@link resolveLocaleFromCoords}.
 *
 * This function may trigger the browser's geolocation permission prompt if the
 * user has not previously made a decision.  Prefer calling this only in
 * response to an explicit user action (e.g. the "I'm in a supported region"
 * button) rather than on page load.  For silent background watching when
 * permission is already granted, use {@link BrowserModule.startGeolocationWatch}
 * (called automatically by `BrowserModule.load`).
 *
 * **Resolves** when the API successfully returns a GPS fix (result may still be
 * {@link SupportedLocale.Unsupported} if the coordinates fall outside all
 * known US/CA bounding boxes).
 *
 * **Rejects** with a {@link GeolocationDetectionError} in every hardware or
 * permission failure case:
 * - The browser does not expose `navigator.geolocation` → {@link GeolocationDetectionErrorCode.NotSupported}
 * - The user denies the permission prompt → {@link GeolocationDetectionErrorCode.PermissionDenied}
 * - The device cannot acquire a GPS fix → {@link GeolocationDetectionErrorCode.PositionUnavailable}
 * - The 10-second acquisition timeout elapses → {@link GeolocationDetectionErrorCode.Timeout}
 *
 * @returns A promise that resolves to the {@link SupportedLocale} for the
 *          user's current GPS position.
 * @throws {GeolocationDetectionError} When the API fails to return coordinates.
 */
export function detectLocaleFromGeolocation(): Promise<SupportedLocale> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(
        new GeolocationDetectionError(
          GeolocationDetectionErrorCode.NotSupported,
        ),
      );
      return;
    }

    navigator.geolocation.getCurrentPosition(
      ({ coords: { latitude, longitude } }) =>
        resolve(resolveLocaleFromCoords(latitude, longitude)),
      (positionError) => {
        let code: GeolocationDetectionErrorCode;
        switch (positionError.code) {
          case positionError.PERMISSION_DENIED:
            code = GeolocationDetectionErrorCode.PermissionDenied;
            break;
          case positionError.TIMEOUT:
            code = GeolocationDetectionErrorCode.Timeout;
            break;
          default:
            code = GeolocationDetectionErrorCode.PositionUnavailable;
        }
        reject(new GeolocationDetectionError(code));
      },
      { timeout: 10000 },
    );
  });
}

/**
 * Detects whether the browser environment belongs to a Contact Center Pro
 * supported locale (United States or Canada).
 *
 * Uses two independent browser signals:
 *   - navigator.language  — user's preferred language / region
 *   - Intl.DateTimeFormat().resolvedOptions().timeZone — system timezone
 *
 * AND logic: both `navigator.language` and the IANA timezone must
 * independently resolve to the same country. If either fails to match,
 * the result is `Unsupported`.
 *
 * Pure function — no side effects, safe to call at module initialisation time.
 */
export function detectSupportedLocale(): SupportedLocale {
  const locale = navigator.language;
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  if (US_LOCALES.has(locale) && US_TIMEZONES.has(timezone)) {
    return SupportedLocale.UnitedStates;
  }

  if (CA_LOCALES.has(locale) && CA_TIMEZONES.has(timezone)) {
    return SupportedLocale.Canada;
  }

  return SupportedLocale.Unsupported;
}
